// @ts-nocheck
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import getIconField, { Icon as IconFieldType, iconOptions } from "@store/Framework/Common/Fields/Toolkit/icon";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import cssProp from "@store/Framework/Common/Utilities/cssProp";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import React, { FC } from "react";
import { css } from "styled-components";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import getLinkTypeField, { LinkType, linkTypeOptions } from "@store/Framework/Common/Fields/Toolkit/linkType";
import { linkIconStyles } from "@store/Themes/common/mixins/link";
import Download from "@store/Components/Toolkit/Icons/Download";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    showSeparator = "showSeparator",
    title = "title",
    titleLink = "titleLink",
    iconPosition = "iconPosition",
    iconType = "iconType",
    mainIcon = "mainIcon",
    customIcon = "customIcon",
    textColor = "textColor",
    items = "items",
    customCSS = "customCSS",
}

const enum itemFields {
    text = "text",
    itemIconType = "itemIconType",
    itemIcon = "itemIcon",
    customItemIcon = "customItemIcon",
    addLink = "addLink",
    linkType = "linkType",
    link = "link",
    fileUrl = "fileUrl",
    openInNewWindow = "openInNewWindow",
}
const linkTypeField = getLinkTypeField(itemFields.linkType);

export interface IconListItemProps {
    fields: {
        [itemFields.text]: string;
        [itemFields.itemIconType]: "icon" | "customImage";
        [itemFields.itemIcon]: IconFieldType;
        [itemFields.customItemIcon]: string;
        [itemFields.addLink]: boolean;
        [itemFields.linkType]: LinkType;
        [itemFields.link]: LinkFieldValue;
        [itemFields.fileUrl]: string;
        [itemFields.openInNewWindow]: boolean;
    };
}

export interface IconListProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.showSeparator]: boolean;
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.iconPosition]: "left" | "right";
        [fields.iconType]: "icon" | "customImage";
        [fields.mainIcon]: IconFieldType;
        [fields.customIcon]: string;
        [fields.textColor]: string;
        [fields.items]: IconListItemProps[];
        [fields.customCSS]: string;
    };
}

export interface IconListStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    titleLink?: LinkPresentationProps;
    horizontalList?: StyledProp;
    horizontalItem?: StyledProp;
    verticalList?: StyledProp;
    verticalItem?: StyledProp;
    itemWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    customIconImage?: InjectableCss;
    text?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const iconListStyles: IconListStyles = {
    wrapper: {
        css: css`
            list-style: none;
            margin: 0;
            padding: 0;
        `,
    },
    titleWrapper: {
        css: spacingStyles(),
    },
    title: {
        forwardAs: "p",
        css: spacingStyles(),
    },
    horizontalItem: css`
        ${inlineSeparatorStyles({ spacing: "1.4em" })}
        display: inline-flex;
    `,
    verticalItem: css`
        ${spacingStyles(null, "0.5em")}
    `,
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    icon: {
        color: "inherit",
        css: linkIconStyles(pxToRem(24), pxToRem(8)),
    },
    customIconImage: {
        css: css`
            ${linkIconStyles(pxToRem(24), pxToRem(8))}

            img {
                display: block;
                width: ${pxToRem(24)};
                height: ${pxToRem(24)};
                object-fit: contain;
            }
        `,
    },
    link: {
        icon: {
            iconProps: {
                css: linkIconStyles(),
            },
        },
    },
};

const styles = iconListStyles;

const defaultCustomCss = `& {
}

.title-wrapper {
}

.list-title {
}

.list-wrapper {
}

.list-item {
}

.list-text {
}

.list-link {
}
`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const mainIconField = getIconField(fields.mainIcon);
const itemIconField = getIconField(itemFields.itemIcon, { isChildDefinition: true, displayName: "Item Icon" });

interface IconListItemComponentProps {
    item: IconListItemProps["fields"];
    linkUrl: string | undefined;
    iconPosition: "left" | "right";
    iconType: "icon" | "customImage";
    resolvedMainIcon: ReturnType<typeof mainIconField.parse>;
    customIconUrl: string | undefined;
    itemStyles: InjectableCss;
}

const IconListItemComponent: FC<IconListItemComponentProps> = ({
    item,
    linkUrl,
    iconPosition,
    iconType,
    resolvedMainIcon,
    customIconUrl,
    itemStyles,
}) => {
    const itemIconOverride =
        item.itemIconType === "icon" && item.itemIcon && item.itemIcon !== iconOptions.none.id
            ? itemIconField.parse(item.itemIcon)
            : undefined;

    const itemCustomIconUrl = item.itemIconType === "customImage" ? parseImageUrl(item.customItemIcon).url : undefined;

    const getIconSrc = () => {
        if (itemCustomIconUrl) {
            return itemCustomIconUrl;
        }
        if (itemIconOverride) {
            return itemIconOverride;
        }
        if (iconType === "customImage" && customIconUrl) {
            return customIconUrl;
        }
        if (iconType === "icon" && resolvedMainIcon) {
            return resolvedMainIcon;
        }
        return undefined;
    };

    const isFileLink = item.addLink && item.linkType === linkTypeOptions.file.id && !!item.fileUrl;

    const finalIconSrc = isFileLink ? Download : getIconSrc();

    const finalCustomIconUrl = isFileLink
        ? undefined
        : itemCustomIconUrl || (!itemIconOverride && iconType === "customImage" ? customIconUrl : undefined);

    const renderedIcon = finalCustomIconUrl ? (
        <StyledWrapper {...styles.customIconImage}>
            <img src={finalCustomIconUrl} alt="" />
        </StyledWrapper>
    ) : (
        <Icon src={getIconSrc()} {...styles.icon} />
    );

    const renderedLink = (
        <Link
            href={linkUrl}
            className="list-link"
            {...styles.link}
            target={item.openInNewWindow ? "_blank" : "_self"}
            {...(!finalCustomIconUrl && finalIconSrc
                ? { icon: { iconProps: { src: finalIconSrc, ...styles.icon }, position: iconPosition } }
                : {})}
        >
            {item.text}
        </Link>
    );

    const itemContent =
        item.addLink && linkUrl ? (
            finalCustomIconUrl ? (
                <StyledWrapper {...styles.itemWrapper}>
                    {iconPosition === "left" && renderedIcon}
                    {renderedLink}
                    {iconPosition === "right" && renderedIcon}
                </StyledWrapper>
            ) : (
                renderedLink
            )
        ) : (
            <Typography className="list-text" {...styles.text}>
                {item.text}
            </Typography>
        );

    return (
        <StyledWrapper as="li" className="list-item" {...itemStyles}>
            {(!item.addLink || !linkUrl) && iconPosition === "left" && renderedIcon}
            {itemContent}
            {(!item.addLink || !linkUrl) && iconPosition === "right" && renderedIcon}
        </StyledWrapper>
    );
};

const IconList: FC<IconListProps> = ({
    fields: {
        direction,
        alignment,
        showSeparator,
        title,
        titleLink,
        iconPosition,
        iconType,
        mainIcon,
        customIcon,
        textColor,
        items = [],
        customCSS,
    },
}) => {
    const { links, hasMissingCategories } = useGetLinks(items, (o: IconListItemProps) => o.fields.link);
    const resolvedTitleLink = useGetLink(titleLink);

    if (hasMissingCategories) {
        return null;
    }

    if (items.length === 0) {
        return null;
    }

    const resolvedMainIcon = mainIconField.parse(mainIcon);
    const customIconUrl = parseImageUrl(customIcon).url;

    const containerCss = {
        css: css`
            ${cssProp("text-align", alignment)}
            color: ${({ theme }) => resolveColor(textColor, theme)};
            ${customCSS}
            li {
                ${direction === "vertical" ? cssProp("justify-content", alignment) : null}
            }
        `,
    };

    const listStyles = {
        css: css`
            ${styles.wrapper?.css}
            ${direction === "horizontal" ? styles.horizontalList : styles.verticalList}
        `,
    };

    const itemStyles = {
        css: css`
            ${styles.itemWrapper?.css}
            ${direction === "horizontal" ? styles.horizontalItem : styles.verticalItem}
            ${direction === "horizontal" && !showSeparator ? `&::after { display: none }` : null}
        `,
    };

    return (
        <StyledWrapper className="icon-list-container" {...containerCss}>
            {title || (resolvedTitleLink && resolvedTitleLink.title) ? (
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    {resolvedTitleLink?.url ? (
                        <Link className="list-title" href={resolvedTitleLink.url} {...styles.titleLink}>
                            {title || resolvedTitleLink.title}
                        </Link>
                    ) : (
                        <Typography className="list-title" {...styles.title}>
                            {title}
                        </Typography>
                    )}
                </StyledWrapper>
            ) : null}
            <StyledWrapper as="ul" className="list-wrapper" {...listStyles}>
                {items.map(({ fields: item }, index) => {
                    const linkUrl = item.linkType === linkTypeOptions.file.id ? item.fileUrl : links[index]?.url;
                    return (
                        <IconListItemComponent
                            key={index}
                            item={item}
                            linkUrl={linkUrl}
                            iconPosition={iconPosition}
                            iconType={iconType}
                            resolvedMainIcon={resolvedMainIcon}
                            customIconUrl={customIconUrl}
                            itemStyles={itemStyles}
                        />
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: IconList,
    definition: {
        group: "Basic",
        icon: "list-ul",
        displayName: "Icon List",
        fieldDefinitions: [
            {
                name: fields.direction,
                displayName: "Direction",
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.showSeparator,
                displayName: "Show Separator",
                tooltip: "Show a pipe separator between items",
                tooltipPosition: "left",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: x => x.fields.direction === "horizontal",
            },
            {
                name: fields.alignment,
                displayName: "Alignment",
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.iconPosition,
                displayName: "Icon Position",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
            },
            {
                name: fields.iconType,
                displayName: "Icon Type",
                editorTemplate: "RadioButtonsField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Icon", value: "icon" },
                    { displayName: "Custom Image", value: "customImage" },
                ],
                defaultValue: "icon",
            },
            {
                ...mainIconField.definition,
                fieldType: "Contextual",
                isVisible: x => x.fields.iconType === "icon",
                tooltip:
                    "Icon applied to all items in the list. You can override this icon for individual items if needed.",
                tooltipPosition: "left",
            },
            {
                name: fields.customIcon,
                displayName: "Custom Icon Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                tooltip:
                    "Upload a custom image to use as an icon. Recommended size is 24x24px. Consider adding some padding to the image itself to ensure it doesn't appear too close to the text.",
                tooltipPosition: "left",
                isVisible: x => x.fields.iconType === "customImage",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                tooltip: "Color for the text and the non-custom images icons.",
                tooltipPosition: "left",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "text.main",
                fieldType: "General",
            },
            {
                name: fields.items,
                displayName: "Items",
                editorTemplate: "ListField",
                getDisplay: (x: { fields: { text?: string } }) => x.fields.text || translate("Item"),
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: itemFields.text,
                        displayName: "Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: itemFields.addLink,
                        displayName: "Add Link",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        variant: "toggle",
                    },
                    {
                        ...linkTypeField.definition,
                        isVisible: (item: { fields: { addLink?: boolean } }) => item.fields.addLink === true,
                    },
                    {
                        name: itemFields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: (item: { fields: { addLink?: boolean; linkType?: string } }) =>
                            item.fields.addLink === true && item.fields.linkType !== linkTypeOptions.file.id,
                    },
                    {
                        name: itemFields.fileUrl,
                        displayName: "File",
                        editorTemplate: "FilePickerField",
                        defaultValue: "",
                        isVisible: (item: { fields: { addLink?: boolean; linkType?: string } }) =>
                            item.fields.addLink === true && item.fields.linkType === linkTypeOptions.file.id,
                    },
                    {
                        name: itemFields.openInNewWindow,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: { fields: { addLink?: boolean } }) => item.fields.addLink === true,
                    },
                    {
                        name: itemFields.itemIconType,
                        displayName: "Icon Type",
                        tooltip: "Override the global icon setting for this item if needed.",
                        tooltipPosition: "left",
                        editorTemplate: "RadioButtonsField",
                        options: [
                            { displayName: "Icon", value: "icon" },
                            { displayName: "Custom Image", value: "customImage" },
                        ],
                        defaultValue: "icon",
                        isVisible: item =>
                            (item.fields.addLink === true && item.fields.linkType !== linkTypeOptions.file.id) ||
                            item.fields.addLink === false,
                    },
                    {
                        ...itemIconField.definition,
                        tooltip: "Override the global icon setting for this item if needed.",
                        tooltipPosition: "left",
                        isVisible: (item: {
                            fields: { itemIconType?: string; addLink?: boolean; linkType?: string };
                        }) =>
                            item.fields.itemIconType === "icon" &&
                            ((item.fields.addLink === true && item.fields.linkType !== linkTypeOptions.file.id) ||
                                item.fields.addLink === false),
                    },
                    {
                        name: itemFields.customItemIcon,
                        displayName: "Custom Item Icon Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tooltip:
                            "Upload a custom image to use as an icon for this item. Recommended size is 24x24px. Overrides the global icon setting.",
                        tooltipPosition: "left",
                        isVisible: (item: { fields: { itemIconType?: string } }) =>
                            item.fields.itemIconType === "customImage",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
